import { useMemo } from 'react';
import { useAdminStats } from '../../hooks/useAdminStats';
import { Spinner } from '../../components/ui/Spinner';

const STATUS_STYLE: Record<string, { bg: string; text: string }> = {
  COMPLETED: { bg: 'bg-green-900/40', text: 'text-green-400' },
  PENDING:   { bg: 'bg-yellow-900/40', text: 'text-yellow-400' },
  EXPIRED:   { bg: 'bg-gray-800', text: 'text-gray-400' },
  CANCELLED: { bg: 'bg-red-900/40', text: 'text-red-400' },
  REJECTED:  { bg: 'bg-red-900/40', text: 'text-red-400' },
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
}

function shortId(id: string) {
  return id.slice(0, 8) + '…';
}

export default function AdminTradesPage() {
  const { data, isLoading, error } = useAdminStats();

  const trades = useMemo(
    () =>
      [...(data?.trades ?? [])].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ),
    [data?.trades]
  );

  // Build userId → username map for display
  const userMap = useMemo(
    () => Object.fromEntries((data?.users ?? []).map((u) => [u.userId, u.username])),
    [data?.users]
  );

  if (isLoading) return <div className="flex justify-center pt-20"><Spinner size={40} /></div>;
  if (error || !data) return <p className="text-red-400">Error al cargar trades.</p>;

  return (
    <div>
      <h1 className="text-lg font-semibold text-white mb-4">
        Trades <span className="text-white/40 text-base font-normal">({trades.length})</span>
      </h1>

      <div className="bg-[#1a1a2e] rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <caption className="sr-only">Lista de trades</caption>
          <thead>
            <tr className="border-b border-[#2a2a3e]">
              {['ID', 'Estado', 'Proponente', 'Receptor', 'Fecha'].map((col) => (
                <th key={col} className="text-left px-4 py-3 text-xs font-semibold text-white/40 uppercase tracking-wide">
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {trades.map((t) => {
              const style = STATUS_STYLE[t.status] ?? { bg: 'bg-gray-800', text: 'text-gray-400' };
              return (
                <tr key={t.tradeId} className="border-b border-[#111122] hover:bg-[#0d0d1c] transition-colors">
                  <td className="px-4 py-3 text-white/40 font-mono text-xs">{shortId(t.tradeId)}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${style.bg} ${style.text}`}>
                      {t.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gold-400">{userMap[t.proposerId] ?? shortId(t.proposerId)}</td>
                  <td className="px-4 py-3 text-gold-400">{userMap[t.targetId] ?? shortId(t.targetId)}</td>
                  <td className="px-4 py-3 text-white/40 text-xs">{formatDate(t.createdAt)}</td>
                </tr>
              );
            })}
            {trades.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-white/30">Sin trades aún.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
